import CryptoJS from 'crypto-js';

export const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
export const IDLE_SESSION_PREFIX = 'hccm-idle-session:';
export const IDLE_TIMEOUT_MESSAGE = '您已连续15分钟未操作，请重新登录';
type IdleSession = {
  lastActiveAt: number;
  ended?: 'logout' | 'timeout';
};

// Same-origin tabs sharing a token share a deadline, without storing another raw token.
let cachedToken = '';
let cachedKey = '';
export function idleSessionKey(token: string) {
  if (cachedToken !== token || !cachedKey) {
    cachedToken = token;
    cachedKey = IDLE_SESSION_PREFIX + CryptoJS.SHA256(token).toString();
  }
  return cachedKey;
}

function readSession(token: string): IdleSession | undefined {
  try {
    const value = localStorage.getItem(idleSessionKey(token));
    if (!value) return undefined;
    const state = JSON.parse(value);
    if (
      !Number.isFinite(state?.lastActiveAt) ||
      state.lastActiveAt <= 0 ||
      (state.ended !== undefined && state.ended !== 'logout' && state.ended !== 'timeout')
    ) {
      return { lastActiveAt: 0, ended: 'timeout' };
    }
    return state;
  } catch {
    // Unreadable/corrupt persistence must not renew an existing session on every refresh.
    return { lastActiveAt: 0, ended: 'timeout' };
  }
}

function writeSession(token: string, state: IdleSession) {
  try {
    localStorage.setItem(idleSessionKey(token), JSON.stringify(state));
  } catch {
    // The next check fails closed if browser storage is unavailable.
  }
}

export function startIdleSession(token: string, freshLogin = false) {
  if (!token) return;
  const state = readSession(token);
  // Persisting a token again (for example after choosing an organization) is not reauthentication.
  if (!state || freshLogin) {
    writeSession(token, { lastActiveAt: Date.now() });
  }
}

export function getIdleSession(token: string): IdleSession {
  const state = readSession(token);
  if (state) return state;
  // Allow sessions that existed before this feature was deployed to start once.
  startIdleSession(token);
  return readSession(token) || { lastActiveAt: 0, ended: 'timeout' };
}

export function isIdleSessionActive(token: string) {
  const state = getIdleSession(token);
  const elapsed = Date.now() - state.lastActiveAt;
  return !state.ended && elapsed >= 0 && elapsed < IDLE_TIMEOUT_MS;
}

export function recordIdleActivity(token: string) {
  // Check BEFORE renewing: the first click after timeout cannot revive the login.
  if (!isIdleSessionActive(token)) return false;
  writeSession(token, { lastActiveAt: Date.now() });
  return true;
}

export function endIdleSession(token: string, reason: 'logout' | 'timeout' = 'logout') {
  if (!token) return;
  const state = readSession(token);
  if (state?.ended) return;
  writeSession(token, { lastActiveAt: state?.lastActiveAt || Date.now(), ended: reason });
}
